import { Link as DSLink, LinkProps as DSLinkProps } from '@s-group/design-system-components/Link/Link'
import { VisuallyHidden } from '@s-group/design-system-components/VisuallyHidden/VisuallyHidden'
import { ReactNode } from 'react'
import { styled } from 'styled-components'

import { SPACE_XXXXSMALL } from '../global/spacing'

export interface LinkProps extends DSLinkProps {
  children?: ReactNode
  ['data-testid']?: string
  disabled?: boolean
  isInline?: boolean
}

const LinkWrap = styled.span<{ $isInline?: boolean }>`
  text-align: left;

  * {
    vertical-align: ${(props) => (props.$isInline ? 'top' : 'middle')};
    display: ${(props) => (props.$isInline ? 'inline' : 'flex')};
    align-items: center;
    gap: ${SPACE_XXXXSMALL};
  }

  &:hover * {
    cursor: pointer;
  }
`

export const Link = ({
  // Always include href to make link focusable
  href = '#',
  icon,
  iconPos = 'before',
  children,
  tabIndex,
  onClick,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
  target,
  color,
  standalone,
  external,
  disabled,
  isInline = false,
}: LinkProps) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (!external) {
      // Prevent default navigation behaviour
      e.preventDefault()
    }
    onClick?.(e)
  }

  return (
    <LinkWrap $isInline={isInline}>
      <DSLink
        href={href}
        standalone={standalone}
        onClick={handleClick}
        className={className}
        tabIndex={tabIndex}
        data-testid={dataTestId}
        icon={icon}
        iconPos={iconPos}
        target={target}
        color={color}
        external={external}
        aria-disabled={disabled}
        aria-labelledby={ariaLabelledBy}
      >
        {children}
        {ariaLabel && <VisuallyHidden>{ariaLabel}</VisuallyHidden>}
      </DSLink>
    </LinkWrap>
  )
}
